import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  Link,
  Toast,
  ToastBody,
  ToastTitle,
  ToastTrigger,
  Toaster,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { getErrorMessage } from '@/lib/api/client';

/**
 * Application toaster built on Fluent's `Toaster`.
 *
 * Feature code never renders a toast directly: it calls `useToaster()` so the
 * intent, timeout and dismiss affordance stay consistent everywhere.
 */

export type ToastIntent = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  title: string;
  description?: ReactNode;
  /** Defaults: 4 s for success/info, 7 s for warning/error. */
  timeout?: number;
  action?: { label: string; onClick: () => void };
}

export interface ToasterApi {
  notify: (intent: ToastIntent, options: ToastOptions) => void;
  success: (title: string, description?: ReactNode) => void;
  error: (title: string, description?: ReactNode) => void;
  warning: (title: string, description?: ReactNode) => void;
  info: (title: string, description?: ReactNode) => void;
  /** Normalizes an unknown rejection into a readable error toast. */
  fromError: (error: unknown, title?: string) => void;
}

const ToasterContext = createContext<ToasterApi | null>(null);

const DEFAULT_TIMEOUT: Record<ToastIntent, number> = {
  success: 4000,
  info: 4000,
  warning: 7000,
  error: 7000,
};

/** Standalone toast body, exported for tests and one-off compositions. */
export function SuccessToast({
  title,
  description,
}: {
  title: string;
  description?: ReactNode;
}): JSX.Element {
  return (
    <Toast>
      <ToastTitle>{title}</ToastTitle>
      {description ? <ToastBody>{description}</ToastBody> : null}
    </Toast>
  );
}

export function ToasterProvider({ children }: { children: ReactNode }): JSX.Element {
  const toasterId = useId('adco-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const notify = useCallback(
    (intent: ToastIntent, options: ToastOptions) => {
      const { title, description, action, timeout } = options;

      dispatchToast(
        <Toast>
          <ToastTitle
            action={
              action ? (
                <ToastTrigger>
                  <Link onClick={action.onClick}>{action.label}</Link>
                </ToastTrigger>
              ) : undefined
            }
          >
            {title}
          </ToastTitle>
          {description ? <ToastBody>{description}</ToastBody> : null}
        </Toast>,
        { intent, timeout: timeout ?? DEFAULT_TIMEOUT[intent] },
      );
    },
    [dispatchToast],
  );

  const api = useMemo<ToasterApi>(
    () => ({
      notify,
      success: (title, description) => notify('success', { title, description }),
      error: (title, description) => notify('error', { title, description }),
      warning: (title, description) => notify('warning', { title, description }),
      info: (title, description) => notify('info', { title, description }),
      fromError: (error, title = 'No se pudo completar la acción') =>
        notify('error', { title, description: getErrorMessage(error) }),
    }),
    [notify],
  );

  return (
    <ToasterContext.Provider value={api}>
      {children}
      <Toaster toasterId={toasterId} position="bottom-end" pauseOnHover pauseOnWindowBlur />
    </ToasterContext.Provider>
  );
}

export function useToaster(): ToasterApi {
  const context = useContext(ToasterContext);
  if (!context) {
    throw new Error('useToaster debe usarse dentro de <ToasterProvider>.');
  }
  return context;
}
